/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {Access} from '../models/Solution';

/**
 * Checks if the provided arguments are enough to identify that an organization
 * has permission to the application. Uses the `access` property from the
 * solution configuration, feature flags from launch darkly, and the service
 * codes that the user has in their product context.
 * @param {Object} access The access solution configuration.
 * @param {Object} featureFlags The feature flags used by the Unified Shell.
 * @param {string[]} serviceCodes The service codes the user has permission to.
 * @returns {boolean} Whether the user has permission to the application.
 */
export const hasPermission = (access: Access = {}, featureFlags: {[key: string]: string} = {}, serviceCodes: string[] = []): boolean => {
  const {
    code = [],
    codeFlagLogicOp = 'OR',
    codeLogicOp = 'OR',
    flag
  } = access;

  // There are no access service codes and no access feature flag, so we can
  // assume that this org is permissioned correctly.
  if ((!code || !code.length) && !flag) {
    return true;
  }

  // Determine if the user has the required feature flag. This is a string
  // because it comes from GQL and they are all strings.
  const hasFlag: boolean = !!flag && featureFlags[flag] === 'true';

  // If there are no service codes set and the flag is true, success.
  if ((!code || !code.length) && hasFlag) {
    return true;
  }

  // Determine if the user has any of the required access service codes.
  const hasCodes: boolean = codeLogicOp === 'OR' ?
    code.some(code => serviceCodes.indexOf(code) > -1) :
    code.every(code => serviceCodes.indexOf(code) > -1);

  return codeFlagLogicOp === 'OR' ? hasCodes || hasFlag : hasCodes && hasFlag;
};
